import dynamic from "next/dynamic";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import { useI18n } from "@/i18n/I18nProvider";
// import CustomHeading from "@/components/custom-heading";

const Banner = dynamic(() => import("@/components/Contact-uae/banner.jsx"));
const SecondSection = dynamic(() =>
  import("@/components/Contact-uae/secondSection.jsx")
);
const ThirdSection = dynamic(() =>
  import("@/components/Contact-uae/thirdSection.jsx")
);
const FormUAE = dynamic(() => import("@/components/Contact-uae/form.jsx"));
const LastSection = dynamic(() =>
  import("@/components/Contact-uae/lastSection.jsx")
);
const PageHead = dynamic(() => import("@/components/PageHead"));

const MAP_HEIGHT = 550;
const MAP_CENTER = { lat: 25.187336414413643, lng: 55.28101926031638 };
const MAP_MARKER_ICON =
  "https://image-solution-no-scale.s3.us-east-2.amazonaws.com/upload/marker.png";

function LazyGoogleMap({ locale }) {
  const containerRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [gmaps, setGmaps] = useState(null);

  const googleMapsApiKey = process.env.NEXT_PUBLIC_GOOGLE_MAP_KEY;

  // 1) Load only when near viewport (reduces LCP/INP)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          io.disconnect();
        }
      },
      // load a bit before it appears (smooth)
      { rootMargin: "250px 0px", threshold: 0.01 }
    );

    io.observe(el);
    return () => io.disconnect();
  }, []);

  // 2) Dynamic import the maps library only when needed
  useEffect(() => {
    if (!shouldLoad) return;
    if (!googleMapsApiKey) return;

    let mounted = true;
    import("@react-google-maps/api").then((m) => {
      if (!mounted) return;
      setGmaps({
        GoogleMap: m.GoogleMap,
        LoadScript: m.LoadScript,
        Marker: m.Marker,
      });
    });

    return () => {
      mounted = false;
    };
  }, [shouldLoad, googleMapsApiKey]);

  const center = useMemo(() => MAP_CENTER, []);
  const mapContainerStyle = useMemo(
    () => ({
      height: `${MAP_HEIGHT}px`,
      width: "100%",
      margin: "auto",
    }),
    []
  );

  const onMarkerClick = useCallback(() => {
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${MAP_CENTER.lat}+${MAP_CENTER.lng}`,
      "_blank"
    );
  }, []);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: "100%",
        height: `${MAP_HEIGHT}px`, // CLS-safe
        borderRadius: "16px",
        overflow: "hidden",
        bgcolor: "#f3f3f3",
        position: "relative",
      }}
    >
      {/* No key? show lightweight fallback */}
      {!googleMapsApiKey ? (
        <Box
          sx={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            p: 2,
          }}
        >
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${MAP_CENTER.lat}+${MAP_CENTER.lng}`}
            target="_blank"
            rel="noreferrer"
            style={{ color: "#2D317F", fontWeight: 700 }}
          >
            Open location on Google Maps
          </a>
        </Box>
      ) : !shouldLoad || !gmaps ? (
        // Skeleton keeps layout stable (CLS) + doesn't block main thread (INP/LCP)
        <Skeleton variant="rectangular" width="100%" height={MAP_HEIGHT} />
      ) : (
        <gmaps.LoadScript
          googleMapsApiKey={googleMapsApiKey}
          language={locale === "ar" ? "ar" : "en"}
        >
          <gmaps.GoogleMap
            id="contact-uae"
            mapContainerStyle={mapContainerStyle}
            center={center}
            zoom={16}
            options={{
              clickableIcons: false,
              fullscreenControl: false,
              streetViewControl: false,
              mapTypeControl: false,
            }}
          >
            <gmaps.Marker
              position={center}
              onClick={onMarkerClick}
              icon={MAP_MARKER_ICON}
            />
          </gmaps.GoogleMap>
        </gmaps.LoadScript>
      )}
    </Box>
  );
}

export default function Home() {
  const { t, locale } = useI18n();

  const contactJsonLd = useMemo(() => {
    return locale === "en"
      ? {
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: "Contact",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Tamanai Arts Office, Business Bay - Office No. 1806",
            addressLocality: "Dubai",
            addressCountry: "UAE",
          },
          telephone: "+971 54 700 4400",
          email: "info@contact.eg",
          url: "https://contact.eg/contact-uae",
        }
      : {
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: "كونتكت",
          address: {
            "@type": "PostalAddress",
            streetAddress: "مكتب تاماناي آرتس بمنطقة الخليج التجاري - مكتب 1806",
            addressLocality: "دبي",
            addressCountry: "الإمارات العربية المتحدة",
          },
          telephone: "+971 54 700 4400",
          email: "info@contact.eg",
          url: "https://contact.eg/ar/contact-uae",
        };
  }, [locale]);

  return (
    <>
      <PageHead
        title={t.pages_main_tags.contactUae.title}
        description={t.pages_main_tags.contactUae.meta_desc}
        structuredData={contactJsonLd}
      />


      <main>
        <Banner />
        <SecondSection />
        <ThirdSection />
        <FormUAE />
        <LastSection />

        <Box className="horizontal-safe-padding vertical-safe-padding">
          <LazyGoogleMap locale={locale} />
        </Box>
      </main>
    </>
  );
}
